import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  IconButton,
  Input,
  Modal,
  Select,
  SimpleTable,
  useToast,
} from '@/components/ui'
import type { TemplateVariable, VariableSource, VariableType } from './types.ts'

const TYPE_OPTIONS: { value: VariableType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Zahl' },
  { value: 'date', label: 'Datum' },
  { value: 'datetime', label: 'Datum & Zeit' },
  { value: 'bool', label: 'Ja/Nein' },
  { value: 'select', label: 'Auswahl' },
  { value: 'entity', label: 'Verknüpfung' },
]

const SOURCE_OPTIONS: { value: VariableSource; label: string }[] = [
  { value: 'start', label: 'Beim Start erfasst' },
  { value: 'form', label: 'Aus Formular-Schritt' },
  { value: 'lookup', label: 'Aus Fremdsystem (Lookup)' },
  { value: 'computed', label: 'Berechnet' },
]

interface VariablesManagerProps {
  open: boolean
  onClose: () => void
  templateId: string
  variables: TemplateVariable[]
  onChange: (next: TemplateVariable[]) => void
}

export function VariablesManager({ open, onClose, templateId, variables, onChange }: VariablesManagerProps) {
  const { toast } = useToast()
  const [varKey, setVarKey] = useState('')
  const [label, setLabel] = useState('')
  const [varType, setVarType] = useState<VariableType>('text')
  const [source, setSource] = useState<VariableSource>('start')
  const [choices, setChoices] = useState('')
  const [saving, setSaving] = useState(false)

  function reset() {
    setVarKey(''); setLabel(''); setVarType('text'); setSource('start'); setChoices('')
  }

  async function handleAdd() {
    if (!varKey.trim() || !label.trim()) {
      toast({ title: 'Schlüssel und Bezeichnung sind erforderlich', tone: 'danger' })
      return
    }
    const options =
      varType === 'select' && choices.trim()
        ? {
            choices: choices
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
              .map((c) => ({ value: c, label: c })),
          }
        : null
    setSaving(true)
    try {
      const { data } = await apiClient.post<{ id: string }>(
        `/prozesse/templates/${templateId}/variables`,
        { varKey: varKey.trim(), label: label.trim(), varType, source, options },
      )
      onChange([
        ...variables,
        {
          id: data.id,
          template_id: templateId,
          var_key: varKey.trim(),
          label: label.trim(),
          var_type: varType,
          options,
          source,
          is_required: 0,
          sort_order: variables.length,
        },
      ])
      reset()
    } catch (err) {
      const msg = err instanceof Error && 'response' in err
        ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? '')
        : ''
      toast({ title: msg || 'Variable konnte nicht angelegt werden', tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    const prev = variables
    onChange(variables.filter((v) => v.id !== id))
    try {
      await apiClient.delete(`/prozesse/templates/${templateId}/variables/${id}`)
    } catch {
      toast({ title: 'Variable konnte nicht gelöscht werden', tone: 'danger' })
      onChange(prev)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Prozess-Variablen" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          Variablen bilden den typisierten Kontext des Prozesses. Sie speisen die
          Gateway-Bedingungen und werden beim Start oder über Formular-Schritte befüllt.
        </p>

        {variables.length > 0 && (
          <SimpleTable>
            <SimpleTable.Head>
              <SimpleTable.Tr>
                <SimpleTable.Th>Bezeichnung</SimpleTable.Th>
                <SimpleTable.Th>Schlüssel</SimpleTable.Th>
                <SimpleTable.Th>Typ</SimpleTable.Th>
                <SimpleTable.Th>Quelle</SimpleTable.Th>
                <SimpleTable.Th className="text-right" />
              </SimpleTable.Tr>
            </SimpleTable.Head>
            <SimpleTable.Body>
              {variables.map((v) => (
                <SimpleTable.Tr key={v.id}>
                  <SimpleTable.Td>{v.label}</SimpleTable.Td>
                  <SimpleTable.Td><code className="text-xs">{v.var_key}</code></SimpleTable.Td>
                  <SimpleTable.Td>{TYPE_OPTIONS.find((t) => t.value === v.var_type)?.label ?? v.var_type}</SimpleTable.Td>
                  <SimpleTable.Td>{SOURCE_OPTIONS.find((s) => s.value === v.source)?.label ?? v.source}</SimpleTable.Td>
                  <SimpleTable.Td className="text-right">
                    <IconButton
                      icon={<Trash2 />}
                      aria-label="Variable löschen"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(v.id)}
                    />
                  </SimpleTable.Td>
                </SimpleTable.Tr>
              ))}
            </SimpleTable.Body>
          </SimpleTable>
        )}

        <div className="rounded-lg border border-neutral-200 p-3 space-y-3">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
            Neue Variable
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Bezeichnung"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z.B. Angebotssumme"
            />
            <Input
              label="Schlüssel"
              value={varKey}
              onChange={(e) => setVarKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
              placeholder="z.B. amount"
              hint="Buchstaben, Ziffern, Unterstrich"
            />
            <Select
              label="Typ"
              value={varType}
              onValueChange={(v) => setVarType(v as VariableType)}
              options={TYPE_OPTIONS}
            />
            <Select
              label="Quelle"
              value={source}
              onValueChange={(v) => setSource(v as VariableSource)}
              options={SOURCE_OPTIONS}
            />
          </div>
          {varType === 'select' && (
            <Input
              label="Auswahlmöglichkeiten"
              value={choices}
              onChange={(e) => setChoices(e.target.value)}
              placeholder="z.B. niedrig, mittel, hoch"
              hint="Kommagetrennt"
            />
          )}
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus className="w-4 h-4" />}
              onClick={handleAdd}
              loading={saving}
            >
              Variable hinzufügen
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
